/**
 * Utilitario para sanitizacao de mensagens de erro.
 *
 * Garante que mensagens tecnicas (stack traces, nomes de exceptions Java,
 * detalhes de I/O, etc.) nunca sejam exibidas ao usuario final.
 * Substitui por mensagens amigaveis e compreensiveis.
 */

/**
 * Padroes que indicam mensagens tecnicas do backend/servidor.
 * Se a mensagem contiver qualquer um desses padroes, sera considerada tecnica.
 */
const PADROES_TECNICOS: RegExp[] = [
  // Exceptions Java
  /java\.\w+/i,
  /javax?\.\w+/i,
  /org\.springframework/i,
  /org\.apache/i,
  /org\.hibernate/i,
  /Exception/,
  /StackTrace/i,
  /NullPointer/i,

  // Erros de rede/IO
  /UnknownHostException/i,
  /ConnectException/i,
  /SocketException/i,
  /SocketTimeoutException/i,
  /ConnectionRefused/i,
  /Connection\s+reset/i,
  /I\/O\s+error/i,
  /timeout/i,

  // Erros HTTP internos
  /Internal\s+Server\s+Error/i,
  /Bad\s+Gateway/i,
  /Service\s+Unavailable/i,
  /Gateway\s+Timeout/i,
  /POST\s+request/i,
  /GET\s+request/i,
  /PUT\s+request/i,
  /DELETE\s+request/i,

  // Respostas HTML em vez de JSON (proxy/servidor retornando HTML)
  /Unexpected\s+token.*is\s+not\s+valid\s+JSON/i,
  /<!DOCTYPE/i,

  // Stack traces e detalhes tecnicos
  /at\s+\w+\.\w+\(/i,
  /Caused\s+by:/i,
  /nested\s+exception/i,
  /\.class/i,
  /localhost:\d+/i,
  /http[s]?:\/\/\S+:\d+/i,
];

/**
 * Verifica se uma mensagem contem conteudo tecnico que nao deve ser exibido ao usuario.
 *
 * @param mensagem Mensagem a verificar
 * @returns true se a mensagem contem termos tecnicos
 */
export function ehMensagemTecnica(mensagem: string): boolean {
  if (!mensagem || mensagem.trim().length === 0) {
    return false;
  }
  return PADROES_TECNICOS.some(padrao => padrao.test(mensagem));
}

/**
 * Sanitiza uma mensagem de erro para exibicao ao usuario.
 *
 * Se a mensagem contem termos tecnicos (exceptions Java, stack traces, erros de I/O, etc.),
 * retorna a mensagem padrao amigavel fornecida. Caso contrario, retorna a mensagem original.
 *
 * @param mensagem Mensagem de erro original (pode ser tecnica)
 * @param mensagemPadrao Mensagem amigavel para substituir caso seja tecnica
 * @returns Mensagem segura para exibir ao usuario
 */
export function sanitizarMensagemErro(mensagem: string | undefined | null, mensagemPadrao: string): string {
  if (!mensagem || mensagem.trim().length === 0) {
    return mensagemPadrao;
  }

  if (ehMensagemTecnica(mensagem)) {
    return mensagemPadrao;
  }

  return mensagem;
}

/**
 * Extrai uma mensagem de erro amigavel de um objeto de erro HTTP.
 *
 * Tenta extrair a mensagem do campo error.message (padrao Spring Boot),
 * e sanitiza para garantir que nao seja tecnica.
 *
 * @param error Objeto de erro (normalmente HttpErrorResponse)
 * @param mensagemPadrao Mensagem amigavel padrao
 * @returns Mensagem segura para exibir ao usuario
 */
export function extrairMensagemErroHttp(error: any, mensagemPadrao: string): string {
  // Tentar extrair mensagem estruturada do backend
  const mensagemBackend = error?.error?.message
    || error?.error?.detail
    || error?.message
    || '';

  return sanitizarMensagemErro(mensagemBackend, mensagemPadrao);
}

/**
 * Extrai a lista de mensagens amigaveis de um erro 400 que carrega um array
 * `erros` no formato `{ campo, mensagem }` (padrao do backend para falhas de
 * regra de negocio — ver API_CONTRACT §2.1/§4).
 *
 * Cada mensagem passa por `sanitizarMensagemErro` (rede de seguranca) usando a
 * `mensagemPadrao` como fallback caso o texto contenha termos tecnicos.
 *
 * Retorna lista vazia quando o erro nao segue esse formato (cabe ao chamador
 * aplicar o tratamento generico nesse caso).
 *
 * @param error         Objeto de erro (normalmente HttpErrorResponse)
 * @param mensagemPadrao Mensagem amigavel de fallback
 * @returns Lista de mensagens prontas para exibir (vazia se formato nao bater)
 */
export function extrairErros400(error: any, mensagemPadrao: string): string[] {
  const erros = error?.error?.erros;
  if (!Array.isArray(erros) || erros.length === 0) {
    return [];
  }

  return erros.map((e: any) => sanitizarMensagemErro(e?.mensagem, mensagemPadrao));
}
